'use client';
import React from 'react';
import Link from 'next/link';
import { CheckCircle, Lock, Play, Clock, Star, ArrowRight, Trophy } from 'lucide-react';
import type { Level } from '@/lib/mockData';
import type { Language } from '@/lib/i18n';
import { t } from '@/lib/i18n';
import { useApp } from '@/lib/AppContext';

interface Props {
  level: Level;
  language: Language;
}

export default function LevelDetailPanel({ level, language }: Props) {
  const { completedLevelTests } = useApp();
  const testResult = completedLevelTests[level.number];
  const allLessonsCompleted = level.lessons.every((ls) => ls.status === 'completed');
  const nextLesson = level.lessons.find((ls) => ls.status === 'in_progress' || ls.status === 'available');
  const lessonToStart = nextLesson ?? level.lessons.find((ls) => ls.status === 'completed');

  return (
    <div className="bg-card rounded-2xl border border-border card-shadow-lg p-5 sticky top-6">
      {/* Level header */}
      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-border">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
          style={{ backgroundColor: `${level.color}20` }}
        >
          {level.icon}
        </div>
        <div>
          <p className="text-xs text-muted-foreground font-500">
            {language === 'fr' ? `Niveau ${level.number}` : `Уровень ${level.number}`}
          </p>
          <h3 className="text-base font-700 text-foreground">{level.title[language]}</h3>
        </div>
      </div>

      <p className="text-sm text-muted-foreground mb-4">{level.description[language]}</p>

      {/* Progress */}
      <div className="mb-4">
        <div className="flex justify-between text-xs mb-1.5">
          <span className="text-muted-foreground font-500">
            {language === 'fr' ? 'Progression' : 'Прогресс'}
          </span>
          <span className="font-700 text-foreground">{level.completionPercent}%</span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${level.completionPercent}%`, backgroundColor: level.color }}
          />
        </div>
      </div>

      {/* Lessons list */}
      <div className="space-y-2 mb-5">
        <p className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2">
          {language === 'fr' ? 'Leçons' : 'Уроки'}
        </p>
        {level.lessons.map((lesson) => {
          const isCompleted = lesson.status === 'completed';
          const isLocked = lesson.status === 'locked';
          const isInProgress = lesson.status === 'in_progress';
          const isAvailable = lesson.status === 'available';
          const isClickable = !isLocked;

          const lessonContent = (
            <div
              className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                isLocked
                  ? 'bg-muted/30 border-border opacity-60'
                  : isCompleted
                  ? 'bg-green-50 border-green-200'
                  : isInProgress
                  ? 'bg-amber-50 border-amber-200' :'bg-primary/5 border-primary/20'
              } ${isClickable ? 'cursor-pointer hover:opacity-90' : ''}`}
            >
              <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0">
                {isCompleted ? (
                  <CheckCircle size={16} className="text-success" />
                ) : isLocked ? (
                  <Lock size={14} className="text-muted-foreground" />
                ) : isInProgress ? (
                  <Clock size={14} className="text-accent" />
                ) : (
                  <Play size={14} className="text-primary" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-500 truncate ${isLocked ? 'text-muted-foreground' : 'text-foreground'}`}>
                  {lesson.title[language]}
                </p>
                {isCompleted && lesson.accuracy && (
                  <p className="text-xs text-success font-500">{lesson.accuracy}% {language === 'fr' ? 'précision' : 'точность'}</p>
                )}
                {isAvailable && (
                  <p className="text-xs text-primary font-500">{language === 'fr' ? 'Disponible' : 'Доступно'}</p>
                )}
              </div>
              <div className="flex items-center gap-1 text-xs font-700 flex-shrink-0" style={{ color: level.color }}>
                <Star size={10} />
                {lesson.xpReward}
              </div>
            </div>
          );

          return isClickable ? (
            <Link key={`detail-lesson-${lesson.id}`} href={`/lesson-screen?lessonId=${lesson.id}`}>
              {lessonContent}
            </Link>
          ) : (
            <div key={`detail-lesson-${lesson.id}`}>
              {lessonContent}
            </div>
          );
        })}
      </div>

      {/* Level Test CTA */}
      {level.isUnlocked && allLessonsCompleted && (
        <div className="mb-3">
          <Link
            href={`/lesson-screen?levelTest=true&level=${level.number}`}
            className={`flex items-center justify-center gap-2 w-full py-3 rounded-xl text-sm font-600 text-white transition-all duration-150 active:scale-95 hover:opacity-90 ${testResult?.passed ? 'bg-green-500' : 'bg-amber-500 hover:bg-amber-600'}`}
          >
            <Trophy size={15} />
            {testResult?.passed
              ? (language === 'fr' ? `Test réussi ✓ (${testResult.score}%) — Réviser` : `Тест сдан ✓ (${testResult.score}%) — Повторить`)
              : (language === 'fr' ? `Passer le test du niveau ${level.number}` : `Пройти тест уровня ${level.number}`)}
          </Link>
        </div>
      )}

      {/* CTA */}
      {level.isUnlocked && lessonToStart ? (
        <Link
          href={`/lesson-screen?lessonId=${lessonToStart.id}`}
          className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-sm font-600 text-white transition-all duration-150 active:scale-95 hover:opacity-90"
          style={{ backgroundColor: level.color }}
        >
          {level.completionPercent === 100
            ? t(language, 'learning_review')
            : level.completionPercent > 0
            ? t(language, 'learning_continue')
            : t(language, 'learning_start')}
          <ArrowRight size={15} />
        </Link>
      ) : level.isUnlocked ? (
        <Link
          href="/lesson-screen"
          className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-sm font-600 text-white transition-all duration-150 active:scale-95 hover:opacity-90"
          style={{ backgroundColor: level.color }}
        >
          {t(language, 'learning_start')}
          <ArrowRight size={15} />
        </Link>
      ) : (
        <div className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-sm font-600 text-muted-foreground bg-muted border border-border">
          <Lock size={14} />
          {t(language, 'learning_locked')}
        </div>
      )}
    </div>
  );
}